import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { menuItemService } from '../../../../services/menuItemService';
import { menuCategoryService } from '../../../../services/menuCategoryService';
import { MenuItemListDTO, MenuCategoryDTO } from '../../../../types';
import { CirclePlus, Eye, RefreshCw, SquarePen, ChefHat, CheckCircle2, Utensils } from 'lucide-react';
import '../RestaurantPage.css';

const MenuItemListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<MenuItemListDTO[]>([]);
  const [categories, setCategories] = useState<MenuCategoryDTO[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    try {
      const res = await menuCategoryService.getAll();
      const d = (res.data as any)?.data;
      setCategories(Array.isArray(d) ? d : (d?.data ?? []));
    } catch {
      // non-fatal
    }
  };

  const loadItems = async (categoryId?: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await menuItemService.getByCategory(categoryId);
      const raw = (res.data as any)?.data ?? (res.data as any) ?? [];
      const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
      setItems(data);
    } catch {
      setError(t('restaurant.menuItem.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await menuItemService.getByCategory(selectedCategoryId);
      const raw = (res.data as any)?.data ?? (res.data as any) ?? [];
      const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
      setItems(data);
    } catch {
      setError(t('restaurant.menuItem.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleCategoryFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value ? parseInt(e.target.value) : undefined;
    setSelectedCategoryId(val);
    loadItems(val);
  };

  const availableCount = items.filter((i) => i.isAvailable).length;
  const activeCount    = items.filter((i) => i.isActive).length;

  const columns: GridColumn[] = [
    {
      key: 'name',
      label: t('restaurant.menuItem.name'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'categoryName',
      label: t('restaurant.menuItem.category'),
      width: '150px',
      render: (value) => <span>{value || '—'}</span>,
    },
    {
      key: 'typeName',
      label: t('restaurant.menuCategory.type'),
      width: '130px',
      hideTablet: true,
      render: (value) =>
        value ? <span className="ims-badge rest-badge-type">{value}</span> : <span>—</span>,
    },
    {
      key: 'price',
      label: t('restaurant.menuItem.price'),
      sortable: true,
      width: '110px',
      render: (value) => <span style={{ fontWeight: 600 }}>{Number(value).toFixed(2)}</span>,
    },
    {
      key: 'isAvailable',
      label: t('restaurant.menuItem.available'),
      width: '120px',
      render: (value) => (
        <span className={`ims-badge ${value ? 'rest-badge-available' : 'rest-badge-unavailable'}`}>
          {value ? t('restaurant.menuItem.available') : t('restaurant.menuItem.unavailable')}
        </span>
      ),
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '110px',
      render: (value) => (
        <span className={`ims-badge ${value ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      key: 'prepTimeMinutes',
      label: t('restaurant.menuItem.prepTime'),
      width: '110px',
      hideMobile: true,
      render: (value) => (value != null ? <span>{value} min</span> : <span>—</span>),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/restaurant/menu-items/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/restaurant/menu-items/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
  ];

  return (
    <Layout>
      <div className="ims-page rest-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('restaurant.menuItem.title')}</h1>
            <p className="ims-list-subtitle">{t('common.manageAndOrganize', 'Manage your menu items and recipes')}</p>
          </div>
          <div className="ims-list-actions">
            <select
              className="ims-form-control"
              value={selectedCategoryId ?? ''}
              onChange={handleCategoryFilter}
              style={{ minWidth: '150px' }}
            >
              <option value="">{t('restaurant.menuItem.allCategories')}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button className="btn-refresh" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/restaurant/menu-items/create')}>
              <CirclePlus size={16} strokeWidth={1.75} />
              {t('restaurant.menuItem.newItem')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-orange">
              <ChefHat size={20} strokeWidth={1.5} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">Total Items</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-green">
              <CheckCircle2 size={20} strokeWidth={1.5} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{availableCount}</span>
              <span className="ims-stat-label">Available Now</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-blue">
              <Utensils size={20} strokeWidth={1.5} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">Active Items</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid
            data={items}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('restaurant.menuItem.noItemsFound')}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>
      </div>
    </Layout>
  );
};

export default MenuItemListPage;
